import React from 'react';
import "./smile-pages.css";

export default function HowDoesSmilingReduceStressAndAnxiety() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">
      <h1>How does Smiling Reduce Stress and Anxiety?</h1>

      <p>
        Smiling reduces stress and anxiety by sending powerful signals to your brain and body that 
        promote <strong>calm, positive emotions</strong>, and even <strong>shift your physiological response </strong> 
        to stress. Here's how it works—backed by science:
      </p>

      <hr />

      {/* Section 1 */}
      <h4>🥰 1. Smiling Activates the Parasympathetic Nervous System</h4>

      <ul>
        <li>
          When you smile, especially a <strong>genuine (Duchenne) smile</strong>, your body activates the 
          <strong> parasympathetic nervous system</strong>—also known as the "rest and digest" system.
        </li>
        <li>This leads to:</li>
        <ul>
          <li>Slower heart rate</li>
          <li>Deeper breathing</li>
          <li>Relaxation of muscles</li>
        </ul>
        <li>These changes directly reduce the <strong>physical symptoms of stress.</strong></li>
      </ul>

      <p><strong>📚 Source:</strong><br />
      Kraft & Pressman (2012) — Participants who smiled during a stressful task had lower heart rates and faster physiological recovery.
      </p>

      <hr />

      {/* Section 2 */}
      <h4>🧠 2. Smiling Triggers the Release of Feel-Good Neurochemicals</h4>

      <ul>
        <li>Smiling prompts your brain to release:</li>
        <ul>
          <li><strong>Endorphins</strong> – natural painkillers that reduce tension</li>
          <li><strong>Dopamine</strong> – enhances pleasure and motivation</li>
          <li><strong>Serotonin</strong> – stabilizes mood and calms anxiety</li>
        </ul>
        <li>
          This neurochemical combination helps <strong>neutralize the stress response</strong> and brings a 
          sense of well-being.
        </li>
      </ul>

      <p><strong>📚 Source:</strong><br />
      Strack, Martin & Stepper (1988) — Participants who held a pen in their mouth (forcing a smile) rated cartoons as funnier, showing mood elevation.
      </p>

      <hr />

      {/* Section 3 */}
      <h4>💡 3. Facial Feedback Loop: Your Face Talks to Your Brain</h4>

      <ul>
        <li>
          The <strong>facial feedback hypothesis</strong> says that your facial expressions don’t just reflect
          emotion — they help create it.
        </li>
        <li>
          When you smile, <strong>even if it’s forced</strong>, your brain interprets the movement as a sign 
          you’re safe and happy, which reduces anxiety.
        </li>
      </ul>

      <hr />

      {/* Section 4 */}
      <h4>🤝 4. Smiling Improves Social Interactions — Reducing Social Anxiety</h4>

      <ul>
        <li>Smiling makes others more likely to respond positively to you.</li>
        <li>
          When social situations feel more welcoming and less threatening, 
          <strong> your anxiety levels decrease.</strong>
        </li>
        <li>
          Positive social feedback reinforces your sense of safety and confidence.
        </li>
      </ul>

      <p><strong>📚 Source:</strong><br />
        Keltner & Bonanno (1997) — Smiling in difficult situations improved emotional resilience and social bonding.
      </p>

      <hr />

      {/* Summary */}
      <h4>✅ Summary: How Smiling Reduces Stress and Anxiety</h4>

      <table className="summary-table">
        <thead>
          <tr>
            <th>Mechanism</th>
            <th>Effect on Stress/Anxiety</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Activates parasympathetic system</td>
            <td>Slows heart rate, relaxes body</td>
          </tr>
          <tr>
            <td>Releases neurochemicals</td>
            <td>Increases happiness, reduces anxiety</td>
          </tr>
          <tr>
            <td>Facial feedback loop</td>
            <td>Tells the brain you're safe and calm</td>
          </tr>
          <tr>
            <td>Improves social interactions</td>
            <td>Builds connection, lowers social stress</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* Keys to remember */}
      <h4>🔑 Keys to Remember</h4>

      <p>
        <strong>Smiling helps reduce stress and anxiety</strong> by calming the body, elevating mood, and 
        telling your brain you're okay—even during tough moments. Smiling is a simple, natural tool for 
        emotional regulation you can use anytime, anywhere. 
      </p>
      <p> 
        <strong> That Smile App is here to help you reduce stress and anxiety.</strong>
      </p>

    </div>
  );
}
